import { sub } from 'date-fns';
import { useEffect, useRef, useState } from 'react';

import { OrderSideEnum } from '@flipster/apis/types';
import { getFillHistoriesApi } from '@flipster/apis/v2/history';
import { FormattedMessage } from '@flipster/i18n';
import { Checkbox } from '@flipster/react/design-system/checkbox';
import { convertDate, dateToEpochString } from '@flipster/utils/datetime';
import segment, { ChartEvent } from '@flipster/utils/segment';

import { IExecutionLineAdapter, type ResolutionString } from '../../../lib/charting_library';
import { ChartStyles } from '../../misc/constants';
import { useTradingViewDataStorage } from '../../misc/data-manager';
import { getTimeByResolution, symbolNameToInfo } from '../../misc/helper';
import TradingView, { TvEventHandler } from '../../trading-view';
import { useTvHeaderContext } from '../index';

interface DrawInfo {
  symbol: string;
  resolution: ResolutionString;
}

export const HistoryPlugin = () => {
  const tv = useTvHeaderContext();

  const helperRef = useRef<HistoryHelper>(null);

  const [checked, setChecked] = useTradingViewDataStorage({
    key: 'historyIndicatorVisible',
  });
  const [tvInfo, setTvInfo] = useState<DrawInfo | null>(null);

  useEffect(() => {
    if (!helperRef.current) helperRef.current = new HistoryHelper(tv);
    const handler: TvEventHandler<'onFirstDataLoaded'> = (info) => {
      helperRef.current?.clean();
      setTvInfo(info);
    };
    tv.on('onFirstDataLoaded', handler);
    return () => tv.off('onFirstDataLoaded', handler);
  }, [tv]);

  useEffect(() => {
    if (!checked.value || !tvInfo) {
      helperRef.current?.clean();
      return;
    }
    helperRef.current?.draw(tvInfo.symbol, tvInfo.resolution);
  }, [checked.value, tvInfo]);

  useEffect(() => {
    return () => {
      helperRef.current?.clean();
    };
  }, []);

  return (
    <Checkbox
      checked={checked.value}
      onChange={(e) => {
        setChecked(e.currentTarget.checked);
        segment.track(ChartEvent.ChartEventToggleClick, {
          event_name: 'History',
          action: e.currentTarget.checked ? 'turn_on' : 'turn_off',
        });
      }}
    >
      <FormattedMessage id='Navigation.r250401.History' />
    </Checkbox>
  );
};

const HistoryMapper = {
  [OrderSideEnum.Buy]: {
    direction: 'buy' as const,
    color: ChartStyles.Color.Up,
  },
  [OrderSideEnum.Sell]: {
    direction: 'sell' as const,
    color: ChartStyles.Color.Down,
  },
};

class HistoryHelper {
  #indicators: IExecutionLineAdapter[] = [];
  #abortController: AbortController | null = null;

  constructor(private readonly tv: TradingView) {}

  draw(symbol: string, resolution: ResolutionString) {
    this.#abortController = new AbortController();
    this.#abortController.signal.addEventListener(
      'abort',
      () => {
        throw new Error('draw abort');
      },
      { once: true },
    );

    this.#draw(symbol, resolution).catch();
  }

  async #draw(symbol: string, resolution: ResolutionString) {
    try {
      this.#abortController?.signal.throwIfAborted();
      if (!this.tv.enabled) return;
      if (this.tv.symbol !== symbol) return;

      const { symbol: symbolWithoutPriceType } = symbolNameToInfo(symbol);
      const {
        data: { history },
      } = await getFillHistoriesApi({
        symbol: symbolWithoutPriceType,
        execType: 'EXEC_TYPE_TRADE',
        startTime: dateToEpochString(sub(new Date(), { days: 30 })), // max 30일
        count: 100,
      });

      this.#indicators.forEach((indicator) => indicator.remove());
      this.#indicators = [];

      const CountLimit = 5;
      let barIndex = 0;
      const counts = {
        [OrderSideEnum.Buy]: 0,
        [OrderSideEnum.Sell]: 0,
      };

      history.forEach((h) => {
        if (!this.tv.activeChart) return;
        if (h.side === OrderSideEnum.Invalid) return;

        const matchTime = convertDate(h.matchTime)!.getTime();
        const orderSecs = Math.floor(matchTime / 1000);
        const newBarIndex = getTimeByResolution(matchTime, resolution);
        if (newBarIndex !== barIndex) {
          barIndex = newBarIndex;
          counts[OrderSideEnum.Buy] = 0;
          counts[OrderSideEnum.Sell] = 0;
        }
        // 최대수 이상은 화살표 노출하지 않음
        if (counts[h.side] >= CountLimit) return;
        const indicatorInfo = HistoryMapper[h.side];
        const indicator = this.tv
          .createExecutionShape()
          .setTime(orderSecs)
          .setDirection(indicatorInfo.direction)
          .setArrowColor(indicatorInfo.color);
        this.#indicators.push(indicator);
        counts[h.side] += 1;
      });
    } catch {
      // abort error || fetch error
    } finally {
      this.#abortController = null;
    }
  }

  clean() {
    if (this.#abortController) {
      this.#abortController?.abort();
      this.#abortController = null;
    }
    this.#indicators.forEach((indicator) => indicator.remove());
    this.#indicators = [];
  }
}
